import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import http from '../services/api';
import { useAuth } from '../context/AuthContext';
import { MapPin, Loader2, CheckCircle2, XCircle, ShieldCheck, Ticket } from 'lucide-react';

/** Route: /events/checkin/:eid — attendee scans event QR, we verify their
 *  device location is within 500 m and award +100 pts (once per event). */
export default function EventCheckin() {
  const { eid } = useParams();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState('idle'); // idle | locating | verifying | ok | err
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => { if (!user) navigate(`/login?redirect=/events/checkin/${eid}`); }, [user, eid, navigate]);

  const start = () => {
    setErr(''); setState('locating');
    if (!('geolocation' in navigator)) { setErr('Location not available'); setState('err'); return; }
    navigator.geolocation.getCurrentPosition(async (pos) => {
      setState('verifying');
      try {
        const r = await http.post(`/events/${eid}/checkin`, { lat: pos.coords.latitude, lng: pos.coords.longitude });
        setResult(r.data); setState('ok'); refresh?.();
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Check-in failed. Move closer to the venue.');
        setState('err');
      }
    }, (geoErr) => {
      setErr(geoErr.code === 1 ? 'Enable location access to check in.' : 'Location read failed. Try again.');
      setState('err');
    }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-8">
      <div className="pbb-card p-6 w-full max-w-md space-y-4" data-testid="event-checkin">
        <div className="flex items-center gap-2">
          <Ticket className="text-green-400" size={22}/>
          <div>
            <div className="font-semibold">Event Check-in</div>
            <div className="text-xs text-gray-400">Verify you're at the venue to earn <b className="text-green-400">+100 pts</b> + 1 loyalty stamp.</div>
          </div>
        </div>
        {state === 'idle' && (
          <button onClick={start} data-testid="event-checkin-start" className="pbb-btn-green w-full inline-flex items-center justify-center gap-2">
            <MapPin size={16}/> Share location & check in
          </button>
        )}
        {(state === 'locating' || state === 'verifying') && (
          <div className="rounded-lg bg-[#0d1f14] border border-[#153b23] p-4 text-sm inline-flex items-center gap-2">
            <Loader2 className="animate-spin text-green-400" size={16}/>
            {state === 'locating' ? 'Reading your location…' : 'Verifying with the event…'}
          </div>
        )}
        {state === 'err' && (
          <div className="rounded-lg bg-red-600/10 border border-red-600/40 p-3 text-sm text-red-300 space-y-3" data-testid="event-checkin-err">
            <div className="inline-flex items-center gap-2"><XCircle size={16}/> {err}</div>
            <button onClick={start} className="px-3 py-2 rounded bg-red-500/20 border border-red-400/40 text-white text-sm">Try again</button>
          </div>
        )}
        {state === 'ok' && result && (
          <div className="space-y-3" data-testid="event-checkin-ok">
            <div className="rounded-lg bg-green-600/10 border border-green-600/40 p-3 text-sm text-green-300 inline-flex items-center gap-2">
              <CheckCircle2 size={16}/> Checked in at {result.eventTitle}. <b>+{result.points_awarded} pts</b> credited!
            </div>
            <Link to="/events" className="pbb-btn-green w-full inline-flex items-center justify-center gap-2">Back to events</Link>
          </div>
        )}
        <div className="text-[11px] text-gray-500 pt-3 border-t border-[#1f1f1f]">
          <ShieldCheck size={11} className="inline mr-1 text-green-400"/> Location is used once to prevent fake attendance and isn't stored.
        </div>
      </div>
    </div>
  );
}
